import { computed } from 'vue'
import { normalizeOrgRole } from './roles.js'
import { ROLE_HIERARCHY, ROLE_PERMISSIONS, KNOWN_ACTIONS, ALLOWED_SCOPES, toRbacContext, evaluatePermission } from '../domain/model.js'

export const RBAC_ERROR = 'RBAC_ERROR'

export const getAssignableRoles = (currentRole) => {
  const role = normalizeOrgRole(currentRole)
  if (role === 'user') return []
  if (role === 'manager') return ['user', 'manager']
  if (role === 'admin') return ['user', 'manager', 'admin']
  return []
}

const hasSuperAdminBypass = (authStore, orgStore) => toRbacContext(authStore, orgStore).isSuperAdmin === true

export const hasRole = (authStore, orgStore, requiredRole) => {
  if (hasSuperAdminBypass(authStore, orgStore)) {
    return true
  }

  const sources = toRbacContext(authStore, orgStore)
  const currentRole = sources.activeOrgRole || sources.user.role
  const normalizedRequired = normalizeOrgRole(requiredRole)
  if (!currentRole || !normalizedRequired) return false

  return ROLE_HIERARCHY[currentRole] >= ROLE_HIERARCHY[normalizedRequired]
}

export const can = (authStore, orgStore, action, context = { scope: 'org' }) => {
  if (hasSuperAdminBypass(authStore, orgStore)) {
    return true
  }

  return evaluatePermission(toRbacContext(authStore, orgStore), action, context)
}

export const checkAccess = (authStore, orgStore, action, context = {}) => {
  if (hasSuperAdminBypass(authStore, orgStore)) {
    return true
  }

  if (!KNOWN_ACTIONS.has(action)) return false
  if (!context || typeof context !== 'object') return false
  if (!ALLOWED_SCOPES.has(context.scope)) return false

  return can(authStore, orgStore, action, context)
}

export const assertPermission = (authStore, orgStore, action, context = {}) => {
  if (checkAccess(authStore, orgStore, action, context)) return true

  const error = new Error(`Access denied for action: ${action}`)
  error.name = RBAC_ERROR
  error.code = RBAC_ERROR
  error.action = action
  error.context = context
  throw error
}

export const usePermissions = (authStore, orgStore) => {
  const role = computed(() => normalizeOrgRole(orgStore?.activeOrgRole))

  return {
    role,
    isAdmin: computed(() => hasRole(authStore, orgStore, 'admin')),
    isManager: computed(() => hasRole(authStore, orgStore, 'manager')),
    isUser: computed(() => hasRole(authStore, orgStore, 'user')),

    can: {
      createLottery: computed(() => checkAccess(authStore, orgStore, 'createLottery', { scope: 'org' })),
      assignUsers: computed(() => checkAccess(authStore, orgStore, 'assignUsers', { scope: 'org' })),
      assignManagers: computed(() => checkAccess(authStore, orgStore, 'assignManagers', { scope: 'org' })),
      uploadBatch: computed(() => checkAccess(authStore, orgStore, 'uploadBatch', { scope: 'org' })),
      drawWinner: computed(() => checkAccess(authStore, orgStore, 'drawWinner', { scope: 'org' })),
      viewLotteries: computed(() => checkAccess(authStore, orgStore, 'viewLotteries', { scope: 'org' })),
      viewBatches: computed(() => checkAccess(authStore, orgStore, 'viewBatches', { scope: 'org' })),
      viewDrawHistory: computed(() => checkAccess(authStore, orgStore, 'viewDrawHistory', { scope: 'org' })),
      manageUsers: computed(() => checkAccess(authStore, orgStore, 'manageUsers', { scope: 'org' })),
      manageSubOrgs: computed(() => checkAccess(authStore, orgStore, 'manageSubOrgs', { scope: 'org' }),
      ),
    }
  }
}

